// File: app/src/utils/trainingBalance.js
//
// How a week of training measures up against evidence-based weekly targets.
// Drives the "What's next" session selector (utils/mixedStyle.js): instead of
// rotating for novelty, it ranks session shapes by what the week is missing.
//
// Targets (docs/training_science.md §3, §8 L2):
//   resistance  2 sessions — strength work for every major muscle group on
//               2+ days a week (WHO 2020; ACSM 2026)
//   anaerobic   1 session  — ~5-6 min of all-out interval work a week (Galpin)
//   aerobic     2 sessions — VO2max work plus easy aerobic volume (Galpin;
//               WHO 150-300 min of moderate activity)
//
// The unit is the SESSION, because that is what history can see reliably:
// workout types and labels, not per-muscle set counts. Muscle-level volume is
// a separate, finer signal (issue #83, L1).

export const WEEKLY_TARGETS = { resistance: 2, anaerobic: 1, aerobic: 2 };

// What each prompt style trains, at the resolution of the weekly targets.
// Mobility and recovery train none of them — they never satisfy a need.
export const STYLE_ADAPTATION = {
  strength: "resistance",
  power: "resistance",
  hypertrophy: "resistance",
  superset: "resistance",
  hiit: "anaerobic",
  tabata: "anaerobic",
  emom: "anaerobic",
  amrap: "anaerobic",
  circuit: "anaerobic",
  functional: "anaerobic",
  conditioning: "aerobic",
  cardio: "aerobic",
};

// Plain-English names for the reason line shown to the user.
const ADAPTATION_LABELS = {
  resistance: "strength",
  anaerobic: "hard interval",
  aerobic: "cardio",
};

// History's `workoutType` is free text: a style key, a joined list, a plan
// label ("Strength + metcon finisher") or a logged activity ("run"). Matched
// loosely on purpose — a false positive only shades a ranking.
const TAG_PATTERNS = [
  [
    "resistance",
    // "heavy a" / "moderate b": programme day names (config/periodisation.js).
    /strength|hypertroph|superset|power|plyo|olympic|weights|lifting|bodybuild|heavy a\b|moderate b\b/,
  ],
  ["anaerobic", /hiit|tabata|metcon|interval|sprint|emom|amrap|chipper|circuit|functional/],
  [
    "aerobic",
    /cardio|conditioning|aerobic|zone ?2|endurance|vo2|\b(run|running|ride|cycling|bike|rower|row|rowing|erg|ski|swim|swimming|walk|hike)\b/,
  ],
];

// VO2max intervals are aerobic training done hard (3-4 min at 90-95% HRmax),
// not the all-out anaerobic work the hard-interval target counts. "VO2max
// Intervals" used to count only as hard intervals because of the word
// "intervals"; it counts as cardio unless all-out work is named as well.
const VO2MAX = /vo2/;
const ALL_OUT = /sprint|all.?out|anaerobic|tabata/;

/** The weekly-target adaptations a free-text workout type counts toward. */
export const adaptationsForWorkoutType = (workoutType) => {
  // "Aerobic power" is VO2max work, not lifting: don't let "power" read as strength.
  const text = String(workoutType || "").toLowerCase().replace(/aerobic power/g, "aerobic");
  const tags = TAG_PATTERNS.filter(([, re]) => re.test(text)).map(([tag]) => tag);
  if (VO2MAX.test(text) && !ALL_OUT.test(text)) {
    return tags.filter((tag) => tag !== "anaerobic");
  }
  return tags;
};

// The weekly targets a session can count toward, in the order the "Counts
// as" picker lists them (#173).
export const COUNTS_AS_TAGS = ["resistance", "anaerobic", "aerobic"];

/**
 * A session's own "Counts as" choice (#173), stored on its plan as
 * `countsAs`: the tags it counts toward ([] for none). Null when it has no
 * choice, so its name decides.
 */
export const readCountsAs = (value) =>
  Array.isArray(value) ? COUNTS_AS_TAGS.filter((t) => value.includes(t)) : null;

/** What a history entry counts toward: its own choice, else its name. */
export const entryAdaptations = (entry) =>
  readCountsAs(entry?.countsAs) ?? adaptationsForWorkoutType(entry?.workoutType);

/**
 * Count the last 7 days of history against each target.
 *
 * @param {Array<{workoutType:string, daysAgo:number, countsAs?:string[]}>} history
 *   summariseTrainingHistory() output (any lookback; only daysAgo < 7 counts).
 * @param {Object} [targets]  Sessions a week per tag; defaults to the general
 *   WEEKLY_TARGETS. computeWeeklyTargets() gives the goal-aware ones.
 * @returns {{ counts: Object, daysSince: Object, targets: Object }}
 */
export const summariseWeeklyBalance = (history, targets = WEEKLY_TARGETS) => {
  const counts = { resistance: 0, anaerobic: 0, aerobic: 0 };
  const daysSince = {
    resistance: Infinity,
    anaerobic: Infinity,
    aerobic: Infinity,
  };
  for (const entry of Array.isArray(history) ? history : []) {
    const days = Number.isFinite(entry?.daysAgo) ? entry.daysAgo : Infinity;
    for (const tag of entryAdaptations(entry)) {
      if (days < 7) counts[tag] += 1;
      if (days < daysSince[tag]) daysSince[tag] = days;
    }
  }
  // The targets travel with the balance, so needFor and describeNeeds measure
  // against this person's goal and days (utils/weeklyTargets.js).
  return { counts, daysSince, targets: targets || WEEKLY_TARGETS };
};

/** 0 (target met) to 1 (nothing done this week) for one adaptation. */
export const needFor = (balance, tag) => {
  const target = (balance?.targets || WEEKLY_TARGETS)[tag];
  if (!target) return 0;
  const done = balance?.counts?.[tag] ?? 0;
  return Math.max(0, target - done) / target;
};

const describeOne = (balance, tag) => {
  const target = (balance.targets || WEEKLY_TARGETS)[tag];
  const done = balance.counts[tag] ?? 0;
  const label = ADAPTATION_LABELS[tag];
  if (done === 0) return `no ${label} sessions in the last 7 days`;
  return `${done} of ${target} ${label} sessions in the last 7 days`;
};

/**
 * One plain sentence on why a shape that trains `tags` is what's next — the
 * unmet targets it addresses, biggest gap first. Null when it meets no need.
 */
export const describeNeeds = (balance, tags) => {
  const unmet = [...new Set(tags)]
    .filter((tag) => needFor(balance, tag) > 0)
    .sort((a, b) => needFor(balance, b) - needFor(balance, a));
  if (unmet.length === 0) return null;
  const parts = unmet.map((tag) => describeOne(balance, tag));
  const sentence = parts.join(", and ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1) + ".";
};
